import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';

/**
 * Countdown component displays the remaining time until a specified end timestamp.
 *
 * @param {object} props - Component props
 * @param {number} props.end - The end time as a timestamp in milliseconds.
 * @param {Function} [props.onEndCallback=Function.prototype] - Optional callback to be called when countdown ends.
 * @returns {JSX.Element|null} The countdown display or null if countdown has ended
 */
const Countdown = ({ end, onEndCallback = Function.prototype }) => {
  const [remaining, setRemaining] = useState(0);

  const intervalRef = useRef(null);

  const update = useCallback(() => {
    const calculatedRemaining = Math.round((end - (new Date()).getTime()) / 1000);

    if (calculatedRemaining <= 0) {
      setRemaining(0); // Set remaining to 0 to hide the countdown
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      onEndCallback();
    } else {
      setRemaining(calculatedRemaining);
    }
  }, [end, onEndCallback]);

  useEffect(() => {
    update();

    intervalRef.current = setInterval(update, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [update]);

  if (remaining > 0) {
    const seconds = remaining % 60;
    const minutes = (remaining - seconds) / 60;

    return (
      <span data-testid="tve-countdown">
        {minutes}
        :
        {`0${seconds}`.slice(-2)}
      </span>
    );
  }

  return null;
};

export { Countdown };
